import React, { useState, useEffect } from 'react';
import { SoulClipItem } from '../studio/studioUtils';
import { FriendUser, EntryTake } from '../../types';
import { fetchFriends } from '../../services/friendService';
import { updateClipShare } from '../../services/shareService';
import { requestClipExport, pollExportReadiness } from '../../services/exportService';
import { CharacterAvatar } from '../common/CharacterAvatar';
import { SocialShareBar } from './SocialShareBar';
import {
  Globe,
  Users,
  Copy,
  Check,
  X,
  Share2,
  Lock,
  MessageSquare,
  Sparkles,
  Scissors,
  Download,
  Loader2,
  Send,
  ExternalLink,
  Crown
} from 'lucide-react';
import { useTokens } from '../../context/TokenContext';

interface ShareSoulClipModalProps {
  isOpen: boolean;
  onClose: () => void;
  clip: SoulClipItem | null;
  takes?: EntryTake[];
  currentUserName?: string;
  onShareUpdated?: (updatedClip: SoulClipItem) => void;
}

export const ShareSoulClipModal: React.FC<ShareSoulClipModalProps> = ({
  isOpen,
  onClose,
  clip,
  takes,
  currentUserName = '',
  onShareUpdated
}) => {
  const [visibility, setVisibility] = useState<'public' | 'direct' | 'external'>('public');
  const [isEditable, setIsEditable] = useState<boolean>(true);
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [friendInput, setFriendInput] = useState<string>('');
  const [shareNote, setShareNote] = useState<string>('');
  const [availableFriends, setAvailableFriends] = useState<FriendUser[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Video Export & Outro Download State
  const { balance, isPro, spend, openPurchaseModal } = useTokens();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isCleanMaster, setIsCleanMaster] = useState<boolean>(false);
  const [exportStatusText, setExportStatusText] = useState<string | null>(null);
  const [exportedUrl, setExportedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && clip) {
      setVisibility(
        clip.shareVisibility === 'external'
          ? 'external'
          : clip.shareVisibility === 'direct'
          ? 'direct'
          : 'public'
      );
      setIsEditable(clip.isEditable !== false);
      setSelectedFriends(clip.sharedToUsers || []);
      setShareNote(clip.shareMessage || '');
      setErrorMsg(null);
      setIsCopied(false);
      setIsExporting(false);
      setExportStatusText(null);
      setExportedUrl(clip.exportedVideoUrl || null);

      fetchFriends(currentUserName).then((friends) => {
        setAvailableFriends(friends);
      });
    }
  }, [isOpen, clip, currentUserName]);

  if (!isOpen || !clip) return null;

  const handleToggleFriend = (friendName: string) => {
    setSelectedFriends((prev) =>
      prev.includes(friendName) ? prev.filter((n) => n !== friendName) : [...prev, friendName]
    );
  };

  const handleAddCustomFriend = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = friendInput.trim();
    if (!clean) return;

    const matchedFriend = availableFriends.find((f) => f.username.toLowerCase() === clean.toLowerCase());
    if (!matchedFriend) {
      setErrorMsg(`@${clean} is not in your accepted mutual friends list. Direct sharing requires permission to prevent spam.`);
      return;
    }

    if (!selectedFriends.some((f) => f.toLowerCase() === clean.toLowerCase())) {
      setSelectedFriends((prev) => [...prev, matchedFriend.username]);
    }
    setErrorMsg(null);
    setFriendInput('');
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/#clip=${clip.id}`;
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2500);
  };

  const triggerBrowserDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadExport = async () => {
    if (!clip) return;

    if (isCleanMaster) {
      if (!isPro && balance < 1) {
        openPurchaseModal('packs');
        return;
      }
      const spendRes = await spend('clean_export', 1, clip.id, `Clean 1080p Master Video Export: ${clip.title}`);
      if (!spendRes.success && !spendRes.coveredBySubscription) {
        openPurchaseModal('packs');
        return;
      }
    }

    setIsExporting(true);
    setErrorMsg(null);
    setExportStatusText(isCleanMaster ? 'Preparing Clean 1080p Master...' : 'Preparing video with 3s outro...');

    try {
      const candidateTakes = (takes && takes.length > 0 ? takes : ((clip as any).takes || clip.entry?.takes || []));
      const activeTakes = candidateTakes.filter((t: any) => t && (t.isActive ?? true));
      const author = currentUserName || clip.creatorName || clip.userName || 'Creator';
      const initialRes = await requestClipExport(clip.id, activeTakes, author);

      if (initialRes.status === 'ready' && initialRes.downloadUrl) {
        setExportedUrl(initialRes.downloadUrl);
        triggerBrowserDownload(initialRes.downloadUrl, `SoulSync_${clip.title.replace(/\s+/g, '_')}.mp4`);
        setExportStatusText('Download started!');
        return;
      }

      // If pending, poll every 3.0s using the distinct takes exportKey
      const polled = await pollExportReadiness(clip.id, (msg) => setExportStatusText(msg), 15, initialRes.exportKey);
      if (polled.status === 'ready' && polled.downloadUrl) {
        setExportedUrl(polled.downloadUrl);
        triggerBrowserDownload(polled.downloadUrl, `SoulSync_${clip.title.replace(/\s+/g, '_')}.mp4`);
        setExportStatusText('Download started!');
      } else {
        setErrorMsg(polled.error || 'Video export could not be completed.');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error exporting clip with outro.');
    } finally {
      setIsExporting(false);
      setTimeout(() => setExportStatusText(null), 3500);
    }
  };

  const handleShareSubmit = async () => {
    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const res = await updateClipShare({
        clipId: clip.id,
        isShared: true,
        shareVisibility: visibility,
        sharedToUsers: visibility === 'direct' ? selectedFriends : [],
        shareMessage: shareNote.trim(),
        isEditable
      });

      if (res.success) {
        const updated: SoulClipItem = {
          ...clip,
          isShared: true,
          shareVisibility: visibility,
          sharedToUsers: visibility === 'direct' ? selectedFriends : [],
          shareMessage: shareNote.trim(),
          isEditable,
          sharedAt: Date.now()
        };
        onShareUpdated?.(updated);
        onClose();
      } else {
        setErrorMsg(res.error || 'Failed to update clip share');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error communicating with server');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnshare = async () => {
    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const res = await updateClipShare({
        clipId: clip.id,
        isShared: false,
        shareVisibility: 'private',
        sharedToUsers: [],
        shareMessage: ''
      });

      if (res.success) {
        const updated: SoulClipItem = {
          ...clip,
          isShared: false,
          shareVisibility: 'private',
          sharedToUsers: [],
          shareMessage: ''
        };
        onShareUpdated?.(updated);
        onClose();
      } else {
        setErrorMsg(res.error || 'Failed to unshare clip');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error communicating with server');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(5, 5, 12, 0.82)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '540px',
          background: 'linear-gradient(135deg, rgba(26, 22, 42, 0.95) 0%, rgba(15, 12, 28, 0.98) 100%)',
          border: '1px solid rgba(168, 85, 247, 0.35)',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(168, 85, 247, 0.15)',
          color: '#ffffff',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.3) 0%, rgba(99, 102, 241, 0.3) 100%)',
                border: '1px solid rgba(168, 85, 247, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Share2 size={18} color="#c084fc" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#f3e8ff' }}>
                Share SoulClip
              </h3>
              <p style={{ margin: 0, fontSize: '0.76rem', color: '#94a3b8' }}>
                Configure community showcase or direct friend collaboration
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'none',
              borderRadius: '8px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Clip Summary Preview Pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            marginBottom: '1.25rem'
          }}
        >
          <img
            src={clip.posterUrl}
            alt={clip.title}
            style={{ width: '48px', height: '32px', borderRadius: '6px', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=480&q=80';
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {clip.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', color: '#a78bfa', marginTop: '2px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Scissors size={11} /> {clip.trimStart.toFixed(1)}s - {clip.trimEnd.toFixed(1)}s
              </span>
              <span>•</span>
              <span>{clip.duration}s duration</span>
            </div>
          </div>
        </div>

        {/* Visibility Options */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#e2e8f0', marginBottom: '8px' }}>
            Distribution Mode
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
            {/* Public Option */}
            <div
              onClick={() => setVisibility('public')}
              style={{
                padding: '10px',
                borderRadius: '12px',
                cursor: 'pointer',
                background: visibility === 'public'
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.22) 0%, rgba(99, 102, 241, 0.15) 100%)'
                  : 'rgba(255, 255, 255, 0.03)',
                border: visibility === 'public'
                  ? '1.5px solid #60a5fa'
                  : '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <Globe size={15} color={visibility === 'public' ? '#60a5fa' : '#94a3b8'} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: visibility === 'public' ? '#93c5fd' : '#cbd5e1' }}>
                  Showcase
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.3 }}>
                Public feed to all SoulSync creators.
              </p>
            </div>

            {/* Direct Option */}
            <div
              onClick={() => setVisibility('direct')}
              style={{
                padding: '10px',
                borderRadius: '12px',
                cursor: 'pointer',
                background: visibility === 'direct'
                  ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.22) 0%, rgba(236, 72, 153, 0.15) 100%)'
                  : 'rgba(255, 255, 255, 0.03)',
                border: visibility === 'direct'
                  ? '1.5px solid #c084fc'
                  : '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <Users size={15} color={visibility === 'direct' ? '#c084fc' : '#94a3b8'} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: visibility === 'direct' ? '#e9d5ff' : '#cbd5e1' }}>
                  Friends
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.3 }}>
                Delivered straight to selected friends.
              </p>
            </div>

            {/* External / Outside Option */}
            <div
              onClick={() => setVisibility('external')}
              style={{
                padding: '10px',
                borderRadius: '12px',
                cursor: 'pointer',
                background: visibility === 'external'
                  ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)'
                  : 'rgba(255, 255, 255, 0.03)',
                border: visibility === 'external'
                  ? '1.5px solid #4ade80'
                  : '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <Share2 size={15} color={visibility === 'external' ? '#4ade80' : '#94a3b8'} />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: visibility === 'external' ? '#86efac' : '#cbd5e1' }}>
                  Share Outside
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.3 }}>
                Download MP4 or send social link.
              </p>
            </div>
          </div>
        </div>

        {/* External & Social Mode Panel */}
        {visibility === 'external' && (
          <div style={{ marginBottom: '1.25rem' }}>
            {/* Download MP4 Action Box */}
            <div
              style={{
                marginBottom: '1rem',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#e2e8f0' }}>
                  Download Video to Your Machine
                </span>
                <span style={{ fontSize: '0.68rem', color: isCleanMaster ? '#facc15' : '#a78bfa' }}>
                  {isCleanMaster ? 'Clean 1080p Master (No Watermark)' : 'Includes 3s Outro Bumper'}
                </span>
              </div>

              {/* Quality & Watermark Toggle */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <button
                  type="button"
                  onClick={() => setIsCleanMaster(false)}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: '8px',
                    background: !isCleanMaster ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                    border: !isCleanMaster ? '1px solid #c084fc' : '1px solid rgba(255, 255, 255, 0.1)',
                    color: !isCleanMaster ? '#ffffff' : '#a1a1aa',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Free (Watermarked)
                </button>
                <button
                  type="button"
                  onClick={() => setIsCleanMaster(true)}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: '8px',
                    background: isCleanMaster ? 'rgba(234, 179, 8, 0.25)' : 'rgba(255, 255, 255, 0.05)',
                    border: isCleanMaster ? '1px solid #facc15' : '1px solid rgba(255, 255, 255, 0.1)',
                    color: isCleanMaster ? '#fef08a' : '#a1a1aa',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <Crown size={12} color={isCleanMaster ? '#facc15' : '#a1a1aa'} />
                  Clean 1080p {isPro ? '(PRO)' : '(1 Token)'}
                </button>
              </div>

              <button
                type="button"
                onClick={handleDownloadExport}
                disabled={isExporting}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  background: isExporting
                    ? 'rgba(168, 85, 247, 0.2)'
                    : isCleanMaster
                    ? 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)'
                    : 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)',
                  border: isCleanMaster ? '1px solid rgba(234, 179, 8, 0.6)' : '1px solid rgba(168, 85, 247, 0.5)',
                  color: isCleanMaster ? '#000000' : '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  cursor: isExporting ? 'wait' : 'pointer',
                  boxShadow: isCleanMaster ? '0 4px 16px rgba(234, 179, 8, 0.35)' : '0 4px 16px rgba(168, 85, 247, 0.35)',
                  transition: 'all 0.2s ease'
                }}
              >
                {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                <span>{exportStatusText || (isCleanMaster ? 'Download Clean Master MP4' : 'Download MP4 (with Outro)')}</span>
              </button>
            </div>

            {/* Social Share Bar with Platform Buttons & Copy Link */}
            <SocialShareBar
              clipId={clip.id}
              title={clip.title}
              creatorName={currentUserName || clip.creatorName}
              customMessage={shareNote}
            />
          </div>
        )}

        {/* Direct Friends Selection */}
        {visibility === 'direct' && (
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#e2e8f0', marginBottom: '6px' }}>
              Select Mutual Friends (2-Way Permissioned)
            </label>

            {/* Friend quick toggles */}
            {availableFriends.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {availableFriends.map((friend) => {
                  const isSelected = selectedFriends.some((f) => f.toLowerCase() === friend.username.toLowerCase());
                  return (
                    <button
                      key={friend.username}
                      type="button"
                      onClick={() => handleToggleFriend(friend.username)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '3px 9px 3px 4px',
                        borderRadius: '9999px',
                        background: isSelected ? 'rgba(168, 85, 247, 0.35)' : 'rgba(255, 255, 255, 0.06)',
                        border: isSelected ? '1px solid #c084fc' : '1px solid rgba(255, 255, 255, 0.12)',
                        color: isSelected ? '#ffffff' : '#cbd5e1',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <CharacterAvatar avatarId={friend.avatarUrl || 'char-maverick'} size="xs" />
                      <span>{friend.username}</span>
                      {isSelected ? <Check size={11} color="#a78bfa" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', background: 'rgba(255, 255, 255, 0.03)', padding: '8px 10px', borderRadius: '8px', marginBottom: '8px' }}>
                No mutual friends added yet. Friendships require 2-way permission. Send a friend request in the Friends tab to collaborate directly.
              </div>
            )}

            {/* Add manual username form */}
            <form onSubmit={handleAddCustomFriend} style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={friendInput}
                onChange={(e) => setFriendInput(e.target.value)}
                placeholder="Type friend's username..."
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  color: '#ffffff',
                  fontSize: '0.8rem',
                  outline: 'none'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: 'rgba(168, 85, 247, 0.25)',
                  border: '1px solid rgba(168, 85, 247, 0.45)',
                  color: '#e9d5ff',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </form>

            {/* Selected recipients tags */}
            {selectedFriends.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {selectedFriends.map((friend) => (
                  <span
                    key={friend}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      background: 'rgba(168, 85, 247, 0.2)',
                      border: '1px solid rgba(168, 85, 247, 0.4)',
                      color: '#f3e8ff',
                      fontSize: '0.74rem'
                    }}
                  >
                    @{friend}
                    <X
                      size={11}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleFriend(friend)}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Allow Collaboration / Editable Toggle (Only applicable for Community / Direct in-app shares) */}
        {visibility !== 'external' && (
          <div
            style={{
              marginBottom: '1.25rem',
              padding: '10px 12px',
              borderRadius: '10px',
              background: isEditable ? 'rgba(34, 197, 94, 0.08)' : 'rgba(148, 163, 184, 0.08)',
              border: isEditable ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(148, 163, 184, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isEditable ? '#4ade80' : '#cbd5e1' }}>
                  {isEditable ? '🤝 Allow Collaboration (Editable)' : '🎬 Clean Scene Template (Non-Editable)'}
                </span>
                <span
                  style={{
                    fontSize: '0.66rem',
                    padding: '1px 6px',
                    borderRadius: '9999px',
                    background: isEditable ? 'rgba(34, 197, 94, 0.2)' : 'rgba(148, 163, 184, 0.2)',
                    color: isEditable ? '#86efac' : '#94a3b8',
                    fontWeight: 700
                  }}
                >
                  {isEditable ? 'Duet Takes Kept' : 'Zero Voice Audio'}
                </span>
              </div>
              <span style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.35 }}>
                {isEditable
                  ? 'Shared clip keeps your voice lines so friends or community can hear them and duet the remaining lines.'
                  : 'Strips your voice stems so importers start with a clean slate (video + BGM + dialogue lines) to voice from scratch.'}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setIsEditable(!isEditable)}
              style={{
                position: 'relative',
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                background: isEditable ? '#22c55e' : 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'background-color 0.2s ease',
                padding: '2px'
              }}
              title={isEditable ? 'Disable editing / collaboration' : 'Enable editing / collaboration'}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  transform: isEditable ? 'translateX(20px)' : 'translateX(0)',
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
            </button>
          </div>
        )}

        {/* Note / Message */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem', fontWeight: 600, color: '#e2e8f0', marginBottom: '6px' }}>
            <MessageSquare size={12} color="#a78bfa" />
            Share Note or Challenge (Optional)
          </label>
          <textarea
            value={shareNote}
            onChange={(e) => setShareNote(e.target.value)}
            placeholder="e.g., 'Recorded line 1! Looking for someone to voice the detective in line 2.'"
            rows={2}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '8px 10px',
              color: '#ffffff',
              fontSize: '0.8rem',
              resize: 'none',
              outline: 'none'
            }}
          />
        </div>

        {/* Error message */}
        {errorMsg && (
          <div style={{ color: '#f87171', fontSize: '0.76rem', marginBottom: '1rem', padding: '6px 10px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.15)' }}>
            {errorMsg}
          </div>
        )}

        {/* Actions Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <button
            type="button"
            onClick={handleCopyLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              padding: '7px 12px',
              color: '#cbd5e1',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
            title="Copy deep link to this clip"
          >
            {isCopied ? <Check size={13} color="#34d399" /> : <Copy size={13} />}
            <span>{isCopied ? 'Copied!' : 'Copy Link'}</span>
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {clip.isShared && (
              <button
                type="button"
                onClick={handleUnshare}
                disabled={isSubmitting}
                style={{
                  padding: '7px 12px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#f87171',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Unshare
              </button>
            )}

            <button
              type="button"
              onClick={handleShareSubmit}
              disabled={isSubmitting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 16px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                border: 'none',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 0 16px rgba(168, 85, 247, 0.4)'
              }}
            >
              <Share2 size={13} />
              <span>{isSubmitting ? 'Saving...' : clip.isShared ? 'Update Share' : 'Share SoulClip'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
